/**
 * @genesis_domain Shared Schemas — Response Contracts
 * @genesis_version V9.1
 * @genesis_intent Response Zod schemas and types for all API endpoints.
 * @genesis_boundaries FORBIDDEN from containing request or ingestion schemas. Pure response contracts only.
 * @genesis_ai_trail Sharded from api.ts during v4.1 Cartography Cleanup (Strike 4).
 */

import { z } from 'zod';

import { IngredientSchema } from './base.js';
import { CategorySchema } from './category.js';
import { RecipeNutritionSchema } from './nutrition.js';
import { RecipeSchema, TaxonomyManifestSchema } from './recipe.js';

// Recipe search result with similarity score for semantic search
export const RecipeSearchResultSchema = RecipeSchema.pick({
    id: true,
    title: true,
    description: true,
    prepTimeMinutes: true,
    cookTimeMinutes: true,
    servings: true,
    userId: true,
    imageUrl: true,
    sourceUrl: true,
    sourceName: true,
    createdAt: true,
    updatedAt: true,
}).extend({
    similarity: z.number().min(0).max(1),
    matchType: z.enum(['exact', 'semantic']).optional(),
    categories: z.array(CategorySchema).optional(),
    nutrition: RecipeNutritionSchema.optional(),
});

export type RecipeSearchResult = z.infer<typeof RecipeSearchResultSchema>;

export const RecipeSearchResponseSchema = z.object({
    results: z.array(RecipeSearchResultSchema),
    query: z.string(),
});

export type RecipeSearchResponse = z.infer<typeof RecipeSearchResponseSchema>;

// Recipe Response Schemas
export const RecipeResponseSchema = RecipeSchema;
export type RecipeResponse = z.infer<typeof RecipeResponseSchema>;

export const RecipesResponseSchema = z.array(RecipeSchema.omit({
    instructions: true,
    aiAnalysis: true,
    auditLog: true,
    ingredients: true,
    nutrition: true,
}));

export type RecipesResponse = z.infer<typeof RecipesResponseSchema>;

// Analysis Response Schema
export const AnalysisResponseSchema = z.object({
    analysis: z.string(),
});

export type AnalysisResponse = z.infer<typeof AnalysisResponseSchema>;

// Share Recipe Response Schema
export const ShareRecipeResponseSchema = z.object({
    recipeId: z.string().uuid(),
    workspaceId: z.string().uuid(),
    userId: z.string().uuid(),
    createdAt: z.string().optional(),
});

export type ShareRecipeResponse = z.infer<typeof ShareRecipeResponseSchema>;

// API Error Schema — aligns with Hono global error handler envelope: { success: false, error: { code, message }, details? }
export const ApiErrorSchema = z.object({
    success: z.literal(false),
    error: z.object({
        code: z.string(),
        message: z.string(),
    }),
    details: z.any().optional(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

export const ApiSuccessSchema = <T extends z.ZodType>(schema: T) =>
    z.object({
        success: z.literal(true),
        data: schema,
    });

export type ApiSuccess<T> = {
    success: true;
    data: T;
};

// Import Recipe Schemas
export const ImportRecipeResponseSchema = z.object({
    title: z.string(),
    description: z.string().optional(),
    instructions: z.string().optional(),
    prepTimeMinutes: z.number().int().optional(),
    cookTimeMinutes: z.number().int().optional(),
    servings: z.number().int().optional(),
    sourceUrl: z.string().url().optional(),
    sourceName: z.string().optional(),
    sourceAuthor: z.string().optional(),
    imageUrl: z.string().url().optional(),
    ingredients: z.array(IngredientSchema),
    _aiMetadata: z.object({
        provider: z.string(),
        model: z.string(),
        extractionMethod: z.enum(['json-ld', 'raw-html', 'markdown', 'vision']),
        rawHtmlLength: z.number().optional(),
        fetchMethod: z.enum(['native', 'jina', 'vision']).optional(),
    }).optional(),
    categories: z.array(z.string()).optional(),
    cuisine: z.string().optional(),
    nutrition: z.record(z.string(), z.unknown()).optional(),
    rawTemperature: z.number().nullable().optional(),
    rawTemperatureUnit: z.string().nullable().optional(),
    temperatureContext: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    rating: z.number().int().min(1).max(5).nullable().optional(),
    sourceRating: z.number().nullable().optional(),
    sourceReviewCount: z.number().int().min(0).nullable().optional(),
}).passthrough();

export type ImportRecipeResponse = z.infer<typeof ImportRecipeResponseSchema>;

// AI Analysis Schemas
export const USDAIngredientSearchSchema = z.object({
    original: z.string(),
    searchQuery: z.string(),
    quantity: z.number(),
    unit: z.string(),
    isDredging: z.boolean().optional(),
});

export type USDAIngredientSearch = z.infer<typeof USDAIngredientSearchSchema>;

export const AIAnalyzeRecipeResponseSchema = z.object({
    scratchpad: z.string(),
    ingredientsForSearch: z.array(USDAIngredientSearchSchema),
    categoryIds: z.array(z.string().uuid()),
    servings: z.coerce.number().int().optional(),
    taxonomyManifest: TaxonomyManifestSchema.optional(),
});

export type AIAnalyzeRecipeResponse = z.infer<typeof AIAnalyzeRecipeResponseSchema>;

export const AnalyzeRecipeResponseSchema = z.object({
    scratchpad: z.string(),
    nutritionPerServing: z.object({
        calories: z.number().int().min(0),
        proteinG: z.number().min(0),
        carbsG: z.number().min(0),
        fatG: z.number().min(0),
        fiberG: z.number().min(0),
        sugarG: z.number().min(0),
        sodiumMg: z.number().int().min(0),
    }),
    nutritionTotal: z.object({
        totalCalories: z.number().int().min(0),
        totalProteinG: z.number().min(0),
        totalCarbsG: z.number().min(0),
        totalFatG: z.number().min(0),
        totalFiberG: z.number().min(0),
        totalSugarG: z.number().min(0),
        totalSodiumMg: z.number().int().min(0),
    }),
    categoryIds: z.array(z.string().uuid()),
    taxonomyManifest: TaxonomyManifestSchema.optional(),
    dataSource: z.string(),
    auditLog: z.string().optional(),
    _aiMetadata: z.object({
        provider: z.string(),
        model: z.string(),
        task: z.string(),
    }).optional(),
    ingredientResults: z
        .array(
            z.object({
                original: z.string(),
                searchQuery: z.string(),
                quantity: z.number(),
                unit: z.string(),
                success: z.boolean(),
                error: z.string().optional(),
            })
        )
        .optional(),
});

export type AnalyzeRecipeResponse = z.infer<typeof AnalyzeRecipeResponseSchema>;

// Workspace Batch Response
export const WorkspaceBatchResponse = z.object({
    success: z.boolean(),
    message: z.string(),
    linkedCount: z.number().int().min(0),
    unlinkedCount: z.number().int().min(0),
    errors: z.array(z.string()).optional(),
});

export type WorkspaceBatchResponseType = z.infer<typeof WorkspaceBatchResponse>;

// Batch Category Response
export const BatchCategoryResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    assignedCount: z.number().int().min(0),
    recipeCount: z.number().int().min(0),
    errors: z.array(z.string()).optional(),
});

export type BatchCategoryResponse = z.infer<typeof BatchCategoryResponseSchema>;

// ─── Re-exports from domain schemas ───
export { type Category } from './category.js';
export { RecipeUpdateSchema, type RecipeUpdate } from './recipe.js';
export {
    WorkspaceCreateSchema,
    WorkspaceUpdateSchema,
    type WorkspaceCreate,
    type WorkspaceUpdate
} from './workspace.js';

